// react-app/src/components/RoleDeck.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, useAnimation } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import "../css/RoleDeck.css";
import TalentModal from "./TalentModal";
import rolesData from "../data/role";
import { useAuth } from "../contexts/AuthContext";

/* ===========================
   Filter options
=========================== */
const roleOptions = [
  "All",
  "Creative",
  "Technical",
  "Management",
  "Administrative",
];

/* ===========================
   Helpers
=========================== */
const norm = (a) => {
  const TAU = Math.PI * 2;
  return ((a + Math.PI) % TAU + TAU) % TAU - Math.PI;
};

// Build a catalog of unique roles from role.js, attach avatars by tier
function buildRoleCatalog(data) {
  const map = new Map();

  for (const r of Array.isArray(data) ? data : []) {
    const name = String(r.role || "").trim();
    if (!name) continue;

    let entry = map.get(name);
    if (!entry) {
      entry = {
        role: name,
        avatars: { Entry: null, Mid: null, Senior: null },
        tiers: new Set(),
      };
      map.set(name, entry);
    }

    const lvl = String(r.talentLevel || "").trim();
    const lvlLower = lvl.toLowerCase();

    // accept both vocabularies
    const isTier1   = /\btier\s*1\b/.test(lvlLower);
    const isTier2   = /\btier\s*2\b/.test(lvlLower);
    const isTier3   = /\btier\s*3\b/.test(lvlLower);

    const isEntry   = /\b(entry|junior|jr\.?)\b/.test(lvlLower) || isTier1;
    const isMid     = /\b(mid|middle|intermediate|assoc(iate)?|ii)\b/.test(lvlLower) || isTier2;
    const isSenior  = /\b(senior|sr\.?)\b/.test(lvlLower) || isTier3;

    // record raw level for display/filtering
    if (lvl) entry.tiers.add(lvl);

    // only set if empty so higher priority can win later if needed
    const setIfEmpty = (slot, value) => {
      if (!entry.avatars[slot]) entry.avatars[slot] = value;
    };

    if (isSenior) setIfEmpty("Senior", r.avatars);
    else if (isMid) setIfEmpty("Mid", r.avatars);
    else if (isEntry) setIfEmpty("Entry", r.avatars);
  }

  const orderTier = (a, b) =>
    (parseInt(a.replace(/\D/g, ""), 10) || 0) -
    (parseInt(b.replace(/\D/g, ""), 10) || 0);

  return Array.from(map.values()).map((e) => ({
    role: e.role,
    avatars: e.avatars,
    tiers: Array.from(e.tiers).sort(orderTier),
  }));
}

/* ===========================
   Role → Work-type classifier
   (Adjust keywords anytime)
=========================== */
function classifyRole(roleName) {
  const r = String(roleName || "").toLowerCase();

  // Creative
  if (
    /artist|animator|illustrator|concept|ui\/?ux|ui|ux|designer|vfx|audio|composer|sound|writer|narrative|3d\b/.test(r)
  ) return "creative";

  // Technical
  if (
    /developer|engineer|unity|unreal|programmer|technical|devops|tools|build|qa|test(ing)?|data|ml|ai/.test(r)
  ) return "technical";

  // Management
  if (
    /manager|lead|director|producer|project|product|pm|team\s*lead|head/.test(r)
  ) return "management";

  // Administrative / Support / Operations
  if (
    /support|customer|player\s*safety|moderation|billing|payments|account\s*recovery|operations|ops|admin(istrative)?/.test(r)
  ) return "administrative";

  // Fallback (treat unknowns as Technical if they sound like dev, else Creative)
  if (/dev|engineer|program/.test(r)) return "technical";
  return "creative";
}

export default function RoleDeck() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // filters
  const [roleFilter, setRoleFilter] = useState("All");

  // search (debounced)
  const [search, setSearch] = useState("");
  const [searchRaw, setSearchRaw] = useState("");

  // carousel state
  const [current, setCurrent] = useState(0);
  const [theta, setTheta] = useState(0);
  const [paused, setPaused] = useState(false);

  // Use a simple string to track modal state to avoid stale refs
  const [selectedRole, setSelectedRole] = useState(null); // string | null
  const isModalOpen = !!selectedRole;

  // User verification state
  const [userData, setUserData] = useState({
    isVerified: false,
    isLoading: true
  });

  // Verification modal state
  const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
  const [verificationAction, setVerificationAction] = useState(null);

  const trackControls = useAnimation();

  // Build role catalog once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const catalog = useMemo(() => buildRoleCatalog(rolesData), []);

  /* ========= Derived data ========= */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const selectedType = roleFilter.toLowerCase();

    return catalog.filter((c) => {
      const type = classifyRole(c.role); // "creative" | "technical" | "management" | "administrative"
      const matchesType = roleFilter === "All" || type === selectedType;
      const matchesSearch = q === "" || c.role.toLowerCase().includes(q);
      return matchesType && matchesSearch;
    });
  }, [catalog, roleFilter, search]);

  // Simple alphabetical sort
  const items = useMemo(() => {
    return [...filtered].sort((a, b) => a.role.localeCompare(b.role));
  }, [filtered]);

  const len = items.length || 1;

  /* ========= Effects ========= */
  // debounce search
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchRaw), 200);
    return () => clearTimeout(id);
  }, [searchRaw]);

  // align theta to keep selected at the front
  useEffect(() => {
    const step = (2 * Math.PI) / len;
    setTheta(-current * step);
  }, [len, current]);

  // Ensure background carousel is fully frozen while modal is open
  useEffect(() => {
    if (isModalOpen) setPaused(true);
  }, [isModalOpen]);

  const DURATION = 0.33;
  const stepAngle = (l) => (Math.PI * 2) / Math.max(1, l);

  const prev = useCallback(() => {
    if (isModalOpen) return; // hard stop when modal open
    const step = stepAngle(len);
    setCurrent((p) => (p - 1 + len) % len);
    setTheta((t) => t + step);
    trackControls.start({ x: [0, 60, 0], rotateY: [0, 6, 0], transition: { duration: DURATION, ease: "easeInOut" } });
  }, [len, trackControls, isModalOpen]);

  const next = useCallback(() => {
    if (isModalOpen) return; // hard stop when modal open
    const step = stepAngle(len);
    setCurrent((p) => (p + 1) % len);
    setTheta((t) => t - step);
    trackControls.start({ x: [0, -60, 0], rotateY: [0, -6, 0], transition: { duration: DURATION, ease: "easeInOut" } });
  }, [len, trackControls, isModalOpen]);

  // auto-scroll every 5s (pause on hover/focus) — stop when modal is open
  useEffect(() => {
    if (len === 0 || paused || isModalOpen) return;
    const id = setInterval(() => next(), 5000);
    return () => clearInterval(id);
  }, [len, paused, isModalOpen, next]);

  // keyboard navigation — disabled while modal is open
  useEffect(() => {
    if (isModalOpen) return; // do not register keys when the modal is open
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, isModalOpen]);

  // swipe handler (disabled when modal is open via drag prop below)
  const handleDragEnd = (_e, info) => {
    if (isModalOpen) return;
    const swipe = Math.abs(info.offset.x) * info.velocity.x;
    if (swipe < -1000) next();
    else if (swipe > 1000) prev();
  };

  // Fetch user verification status
  const fetchUserData = async () => {
    if (!currentUser?.uid) {
      setUserData({ isVerified: false, isLoading: false });
      return;
    }

    try {
      setUserData(prev => ({ ...prev, isLoading: true }));
      
      const response = await fetch(`https://exodiauno-896735867.development.catalystserverless.com/server/exodia_uno_function/user?userId=${currentUser.uid}`);
      
      if (response.ok) {
        const result = await response.json();
        const user = result.data;
        
        setUserData({
          isVerified: user?.is_verified || user?.email_verified || false,
          isLoading: false
        });
      } else {
        setUserData({ isVerified: false, isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserData({ isVerified: false, isLoading: false });
    }
  };

  useEffect(() => {
    if (currentUser?.uid) {
      fetchUserData();
    }
  }, [currentUser?.uid]);

  // Verification check function for talent unlocking
  const checkVerificationAndProceed = (actionName, actionFunction) => {
    if (userData.isLoading) {
      // Show loading modal instead of alert
      setVerificationAction({ name: actionName, function: actionFunction });
      setShowVerificationPrompt(true);
      return;
    }

    if (!userData.isVerified) {
      // Show custom gamified modal instead of browser confirm
      setVerificationAction({ name: actionName, function: actionFunction });
      setShowVerificationPrompt(true);
      return;
    }

    // User is verified, proceed with the action
    actionFunction();
  };

  /* ===========================
     Render
  =========================== */
  return (
    <main className="roledeck" aria-busy={isModalOpen ? "true" : "false"}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideInUp {
            from { 
              opacity: 0;
              transform: translateY(30px) scale(0.9);
            }
            to { 
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes glow {
            0%, 100% { box-shadow: 0 0 5px rgba(102, 126, 234, 0.5); }
            50% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.8), 0 0 30px rgba(102, 126, 234, 0.6); }
          }
        `}
      </style>
      <Header />
      <div className="roledeck-header">
        <h1 className="title">Role Deck</h1>
        <p className="subtitle">Swipe or click to browse roles</p>
      </div>

      {/* Search & work-type filter */}
      <div className="roledeck-toolbar" aria-hidden={isModalOpen}>
        <div className="roledeck-searchbar">
          <span className="roledeck-search-icon" aria-hidden>🔎</span>
          <input
            type="text"
            className="roledeck-search-input"
            placeholder="Search roles..."
            value={searchRaw}
            onChange={(e) => setSearchRaw(e.target.value)}
            aria-label="Search roles"
            disabled={isModalOpen}
          />
        </div>

        <div className="roledeck-filters">
          <div className="dropdown-filter">
            <label className="dropdown-label">Work type: </label>
            <select
              className="dropdown-native"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              disabled={isModalOpen}
            >
              {roleOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* CENTERING WRAPPER */}
      <div className="deck-center">
        <div className={`carousel-frame ${isModalOpen ? "frozen" : ""}`}>
          {len > 0 && (
            <>
              <button
                className="carousel-arrow left"
                onClick={prev}
                aria-label="Previous role"
                aria-disabled={isModalOpen}
                disabled={isModalOpen}
              >
                ←
              </button>
              <button
                className="carousel-arrow right"
                onClick={next}
                aria-label="Next role"
                aria-disabled={isModalOpen}
                disabled={isModalOpen}
              >
                →
              </button>
            </>
          )}

          {/* Carousel */}
          <motion.div
            className="carousel"
            drag={isModalOpen ? false : "x"}
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={isModalOpen ? undefined : handleDragEnd}
            animate={trackControls}
            transition={{ duration: 0.33, ease: [0.22, 1, 0.36, 1] }}
            onMouseEnter={() => !isModalOpen && setPaused(true)}
            onMouseLeave={() => !isModalOpen && setPaused(false)}
            onFocus={() => !isModalOpen && setPaused(true)}
            onBlur={() => !isModalOpen && setPaused(false)}
            aria-roledescription="carousel"
            aria-label="Role cards"
          >
            {items.length === 0 ? (
              <div className="no-results">No roles found.</div>
            ) : (
              items.map((it, i) => {
                const lenSafe = Math.max(1, len);
                const STEP = (Math.PI * 2) / lenSafe;
                const angle = norm(i * STEP + theta);

                const slotRaw = Math.round(angle / STEP);
                const slot = Math.max(-2, Math.min(2, slotRaw));

                const GAP_STEP = 160;
                const SPREAD_X = 0.7;

                const RING = 360;
                const xArc = RING * Math.sin(angle);
                const zArc = -RING * (1 - Math.cos(angle));

                const SLOT = {
                  "-2": { rot: -22, zPush: -100, scale: 0.80,  opacity: 0.66, blur: 0.5 },
                  "-1": { rot: -12, zPush:  -50, scale: 1,     opacity: 0.88, blur: 0   },
                  "0":  { rot:   0, zPush:    0, scale: 1.20,  opacity: 1.00, blur: 0   },
                  "1":  { rot:  12, zPush:  -50, scale: 1,     opacity: 0.88, blur: 0   },
                  "2":  { rot:  22, zPush: -100, scale: 0.80,  opacity: 0.66, blur: 0.5 },
                }[String(slot)];

                const x = xArc * SPREAD_X + slot * GAP_STEP;
                const z = zArc + SLOT.zPush;
                const rotateY = SLOT.rot;
                const scale = SLOT.scale;
                const visible = Math.abs(slotRaw) <= 2.2;
                const opacity = visible ? SLOT.opacity : 0;
                const filter = visible ? `blur(${SLOT.blur || 0}px)` : "blur(0.6px) opacity(0)";
                const zIndex = visible ? (100 + (3 - Math.abs(slot)) * 10) : 10;
                const isActive = i === current;

                const img = (it.avatars?.Senior || it.avatars?.Mid || it.avatars?.Entry || "");

                return (
                  <div key={`${it.role}-${i}`} className="card-anchor" style={{ zIndex }}>
                    <motion.button
                      className={`talent-card ${isActive ? "active" : ""}`}
                      data-pos={slot}
                      data-active={isActive ? "true" : "false"}
                      style={{ transformOrigin: "50% 50%" }}
                      initial={false}
                      animate={{ x, z, rotateY, scale, opacity, filter }}
                      transition={{ duration: 0.33, ease: [0.22, 1, 0.36, 1] }}
                      whileHover={{ z: z + 18, scale: Math.min(scale + 0.015, 1.05), transition: { duration: 0.18 } }}
                      whileTap={{ scale: Math.max(scale - 0.02, 0.9) }}
                      onClick={() => {
                        if (isModalOpen) return;
                        if (i === current) {
                          // Check verification before opening talent modal
                          checkVerificationAndProceed('unlock talent', () => {
                            setSelectedRole(it.role);
                          });
                        } else {
                          const diff = (i - current + lenSafe) % lenSafe;
                          const alt  = diff - lenSafe;
                          const move = Math.abs(alt) < diff ? alt : diff;
                          setCurrent(i);
                          setTheta((t) => t - move * STEP);
                        }
                      }}
                      aria-pressed={isActive}
                      aria-label={it.role}
                      disabled={isModalOpen}
                    >
                      <div className="card-shine" aria-hidden />
                      <div className="card-floorShadow" aria-hidden />

                      <div className="card-layer layer-avatar">
                        {img ? (
                          <img src={img} alt={`${it.role} (Senior Avatar)`} className="talent-avatar" />
                        ) : (
                          <div className="talent-avatar placeholder" aria-hidden />
                        )}
                      </div>

                      <div className="card-layer layer-title">
                        <h3 className="role">{it.role}</h3>
                      </div>
                    </motion.button>
                  </div>
                );
              })
            )}
          </motion.div>
        </div>
      </div>

      {/* dots */}
      {items.length > 0 && (
        <div className="dots" role="tablist" aria-label="Role slides">
          {items.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === current}
              className={`dot ${i === current ? "active" : ""}`}
              onClick={() => {
                if (isModalOpen) return;
                const step = (2 * Math.PI) / len;
                const diff = ((i - current + len) % len);
                const alt  = diff - len;
                const move = Math.abs(alt) < diff ? alt : diff;
                setCurrent(i);
                setTheta((t) => t - move * step);
              }}
              aria-label={`Go to slide ${i + 1}`}
              aria-disabled={isModalOpen}
              disabled={isModalOpen}
            />
          ))}
        </div>
      )}

      {/* Gamified Verification Prompt Modal */}
      {showVerificationPrompt && (
        <div 
          className="gamified-verification-prompt-overlay" 
          onClick={() => setShowVerificationPrompt(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <div 
            className="gamified-verification-prompt" 
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '450px',
              width: '90%',
              color: 'white',
              position: 'relative',
              boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
              border: '2px solid rgba(255,255,255,0.2)',
              textAlign: 'center',
              animation: 'slideInUp 0.4s ease-out',
              transform: 'scale(1)',
              transition: 'all 0.3s ease'
            }}
          >
            <button 
              className="close-btn" 
              onClick={() => setShowVerificationPrompt(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '35px',
                height: '35px',
                color: 'white',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.3)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.2)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>
            
            <div style={{ fontSize: '3rem', marginBottom: '15px', animation: 'bounce 2s infinite' }}>
              {userData.isLoading ? '⏳' : '🔐'}
            </div>
            
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '700',
              margin: '0 0 15px 0',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {userData.isLoading ? 'Checking Status...' : 'Verification Required'}
            </h2>
            
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '15px',
              padding: '15px',
              marginBottom: '20px',
              backdropFilter: 'blur(10px)'
            }}>
              <p style={{ 
                fontSize: '14px',
                lineHeight: '1.5',
                margin: '0'
              }}>
                {userData.isLoading 
                  ? 'Please wait while we check your verification status...'
                  : `To ${verificationAction?.name}, you need to complete account verification first.`
                }
              </p>
            </div>
            
            {!userData.isLoading && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                marginTop: '20px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => {
                    setShowVerificationPrompt(false);
                    navigate('/profile');
                    setTimeout(() => {
                      // Open verification modal after navigation
                      window.dispatchEvent(new CustomEvent('openVerificationModal'));
                    }, 100);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)',
                    border: 'none',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '25px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)',
                    minWidth: '120px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 107, 0.6)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(255, 107, 107, 0.4)';
                  }}
                >
                  Start Verification
                </button>
                
                <button
                  onClick={() => setShowVerificationPrompt(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '25px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    minWidth: '120px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.3)';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.2)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  Maybe Later
                </button>
              </div>
            )}
            
            {userData.isLoading && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '20px',
                gap: '10px'
              }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <span style={{ fontSize: '14px', opacity: '0.8' }}>Loading...</span>
              </div>
            )}
            
            {/* Glow effect */}
            <div style={{
              position: 'absolute',
              top: '-2px',
              left: '-2px',
              right: '-2px',
              bottom: '-2px',
              background: 'linear-gradient(135deg, #667eea, #764ba2, #ff6b6b)',
              borderRadius: '22px',
              zIndex: -1,
              opacity: '0.3',
              animation: 'glow 2s ease-in-out infinite alternate'
            }}></div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <TalentModal
          talent={{ role: items[current]?.role }}
          currentIndex={current}
          setCurrentIndex={setCurrent}
          total={items.length}
          onClose={() => {
            setSelectedRole(null);
            setPaused(false);
          }}
        />
      )}
    </main>
  );
}
